import { Link, useParams, Navigate } from 'react-router-dom'
import { HELP_ARTICLES } from './Help'

/**
 * Reusa o renderer de markdown do LegalPage — mesmo subset (h1/h2/h3,
 * bold, italic, link, ul, tabela markdown não suportada por enquanto).
 */
function renderMarkdown(md: string): JSX.Element[] {
  const out: JSX.Element[] = []
  const lines = md.split('\n')
  let listBuffer: string[] = []
  let paraBuffer: string[] = []
  let inTable = false
  let tableBuffer: string[][] = []

  function flushList(key: string) {
    if (listBuffer.length) {
      out.push(
        <ul key={key} className="list-disc pl-6 space-y-1 my-3 text-gray-700">
          {listBuffer.map((item, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: inline(item) }} />
          ))}
        </ul>
      )
      listBuffer = []
    }
  }
  function flushPara(key: string) {
    if (paraBuffer.length) {
      const text = paraBuffer.join(' ').trim()
      if (text) out.push(<p key={key} className="text-gray-700 leading-relaxed my-2" dangerouslySetInnerHTML={{ __html: inline(text) }} />)
      paraBuffer = []
    }
  }
  function flushTable(key: string) {
    if (tableBuffer.length === 0) return
    const [headers, ...rows] = tableBuffer
    out.push(
      <div key={key} className="my-4 overflow-x-auto">
        <table className="w-full text-sm border border-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {headers.map((h, i) => (
                <th key={i} className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b border-gray-200" dangerouslySetInnerHTML={{ __html: inline(h) }} />
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} className="border-b border-gray-100 last:border-0">
                {row.map((cell, ci) => (
                  <td key={ci} className="px-3 py-2 text-gray-700" dangerouslySetInnerHTML={{ __html: inline(cell) }} />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
    tableBuffer = []
    inTable = false
  }
  function inline(s: string): string {
    return s
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/_(.+?)_/g, '<em>$1</em>')
      .replace(/`([^`]+)`/g, '<code class="text-xs bg-gray-100 px-1 py-0.5 rounded">$1</code>')
      .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-indigo-600 hover:underline">$1</a>')
  }

  lines.forEach((line, i) => {
    const key = `l${i}`
    const trimmed = line.trim()

    // Detecta tabela markdown (| coluna1 | coluna2 |)
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      flushPara(key + 'p'); flushList(key + 'l')
      const cells = trimmed.slice(1, -1).split('|').map(c => c.trim())
      // Linha separadora (| --- | --- |) — ignora
      if (cells.every(c => /^:?-+:?$/.test(c))) return
      tableBuffer.push(cells)
      inTable = true
      return
    } else if (inTable) {
      flushTable(key + 't')
    }

    if (line.startsWith('### ')) {
      flushPara(key + 'p'); flushList(key + 'l')
      out.push(<h3 key={key} className="text-base font-semibold text-gray-900 mt-5 mb-2">{line.slice(4)}</h3>)
    } else if (line.startsWith('## ')) {
      flushPara(key + 'p'); flushList(key + 'l')
      out.push(<h2 key={key} className="text-lg font-semibold text-gray-900 mt-6 mb-2">{line.slice(3)}</h2>)
    } else if (line.startsWith('# ')) {
      flushPara(key + 'p'); flushList(key + 'l')
      out.push(<h1 key={key} className="text-3xl font-bold text-gray-900 mt-2 mb-4">{line.slice(2)}</h1>)
    } else if (line.startsWith('- ')) {
      flushPara(key + 'p')
      listBuffer.push(line.slice(2))
    } else if (line.startsWith('> ')) {
      flushPara(key + 'p'); flushList(key + 'l')
      out.push(<blockquote key={key} className="border-l-4 border-indigo-200 pl-4 my-3 italic text-gray-700" dangerouslySetInnerHTML={{ __html: inline(line.slice(2)) }} />)
    } else if (trimmed === '') {
      flushPara(key + 'p'); flushList(key + 'l')
    } else {
      flushList(key + 'l')
      paraBuffer.push(line)
    }
  })
  flushTable('endT'); flushPara('endP'); flushList('endL')
  return out
}

export default function HelpArticle() {
  const { slug } = useParams<{ slug: string }>()
  const article = HELP_ARTICLES.find(a => a.slug === slug)

  if (!article) return <Navigate to="/ajuda" replace />

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-indigo-600">PAKT</Link>
          <Link to="/ajuda" className="text-sm text-indigo-600 hover:text-indigo-800 transition">
            ← Central de Ajuda
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <article className="bg-white rounded-xl border border-gray-200 p-8">
          {renderMarkdown(article.content)}
        </article>

        <div className="mt-6 bg-indigo-50 border border-indigo-100 rounded-lg p-4 text-sm">
          <strong className="text-indigo-900">Não resolveu?</strong>{' '}
          <span className="text-indigo-800">
            Manda e-mail pra{' '}
            <a href="mailto:suporte@pakt.io" className="underline">suporte@pakt.io</a>
            {' '}— respondemos em até 24h em dias úteis.
          </span>
        </div>
      </main>
    </div>
  )
}
